import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import Reveal from "@/components/Reveal";
import { ArrowDown } from "lucide-react";

const stages = [
  { title: "Positioning", note: "Define what you should be known for — and how you should communicate it." },
  { title: "Content", note: "Turn expertise, opinions and experience into a consistent content system." },
  { title: "Creative", note: "Turn ideas into carousels, graphics and visual content people actually stop to consume." },
  { title: "Distribution", note: "Build consistency and reach the right audience on LinkedIn." },
  { title: "Audience", note: "The right people, growing around what you know." },
  { title: "Opportunity", note: "Attention turning into audience, relationships and business opportunities." },
];

export default function LinkedInSystem() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 65%"] });

  return (
    <section id="system" className="bg-[#F3F0E8] text-[#09090B] scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-28 sm:py-40">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#118C4F]">the linkedin system</p>
          <h2
            data-testid="system-heading"
            className="font-serif-d font-black tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-8xl mt-6 max-w-5xl"
          >
            A LinkedIn presence is{" "}
            <span className="italic font-semibold text-[#09090B]/40">a system.</span>
          </h2>
          <p className="text-base sm:text-xl text-[#09090B]/60 leading-relaxed mt-8 max-w-2xl">
            I don't manage profiles. I build the system around your expertise,
            your point of view and the people you want to reach. Hover each stage.
          </p>
        </Reveal>

        <div ref={ref} className="relative mt-20 sm:mt-28 max-w-4xl">
          <div className="absolute left-[7px] sm:left-[9px] top-2 bottom-2 w-px bg-[#09090B]/15" aria-hidden="true" />
          <motion.div
            style={{ scaleY: scrollYProgress }}
            className="absolute left-[7px] sm:left-[9px] top-2 bottom-2 w-px bg-[#118C4F] origin-top"
            aria-hidden="true"
          />

          {stages.map((s, i) => (
            <Reveal key={s.title} delay={0.05}>
              <div data-testid={`system-stage-${i + 1}`} className="group relative pl-12 sm:pl-16 pb-14 sm:pb-16 last:pb-0">
                <span
                  className={`absolute left-0 top-2 w-[15px] h-[15px] sm:w-[19px] sm:h-[19px] rounded-full border-2 transition-colors duration-500 ${
                    "border-[#09090B]/30 bg-[#F3F0E8] group-hover:border-[#118C4F] group-hover:bg-[#118C4F]"
                  }`}
                  aria-hidden="true"
                />
                <div className="flex flex-wrap items-baseline gap-x-6">
                  <span className="text-xs tracking-[0.25em] text-[#118C4F]">0{i + 1}</span>
                  <h3 className="font-serif-d font-black tracking-tight uppercase text-3xl sm:text-5xl lg:text-6xl group-hover:text-[#118C4F] transition-colors duration-500 cursor-default">
                    {s.title}
                  </h3>
                </div>
                <p className="text-sm sm:text-lg leading-relaxed max-w-xl mt-1 sm:mt-2 transition-all duration-500 text-[#09090B]/65 opacity-100 lg:text-[#09090B]/0 lg:opacity-0 lg:-translate-y-1 lg:group-hover:text-[#09090B]/65 lg:group-hover:opacity-100 lg:group-hover:translate-y-0">
                  {s.note}
                </p>
                {i < stages.length - 1 && (
                  <ArrowDown className="absolute left-12 sm:left-16 -bottom-1 w-4 h-4 text-[#118C4F]/50" aria-hidden="true" />
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
